// CTA Handler utilities for handling Call-to-Action button clicks

/**
 * Handle CTA button actions
 * @param {object} cta - The CTA object with type, action, context, etc.
 * @param {string} conversationId - Current conversation ID
 */
export const handleCTAAction = (cta, conversationId) => {
  // Guard: Check for missing type field
  if (!cta.type) {
    console.error('[CTA Handler] CRITICAL: CTA missing type field. Full CTA object:', cta);
    // Fall back to action-based routing for legacy CTAs
    if (cta.action) {
      console.warn('[CTA Handler] Attempting fallback routing based on action field...');
    }
  }

  // Handle legacy actions first (for backward compatibility)
  switch (cta.action) {
    case 'START_APPLICATION':
      handleStartApplication(cta, conversationId);
      return;
    case 'CONTINUE_CHAT':
      handleContinueChat(cta, conversationId);
      return;
    default:
      // No legacy action - fall through to type-based handling below
      break;
  }

  // Handle new type-based CTAs
  switch (cta.type) {
    case 'application':
      handleApplicationCTA(cta, conversationId);
      break;
    case 'question':
      handleQuestionCTA(cta, conversationId);
      break;
    case 'resource':
      handleResourceCTA(cta);
      break;
    case 'external':
      handleExternalCTA(cta);
      break;
    default:
      console.warn('[CTA Handler] Unknown CTA type:', cta.type, '- Full CTA:', cta);
  }
};

/**
 * Navigate to wizard-based soknad flow
 */
const handleStartApplication = (cta, conversationId) => {
  // Redirect to new wizard flow instead of legacy application builder
  window.location.href = '/soknad/hjelpestonad';
};

/**
 * Handle legacy CONTINUE_CHAT action - now routes to auto-submit behavior
 * If question text is available, auto-submits; otherwise focuses input
 */
const handleContinueChat = (cta, conversationId) => {
  // Check if we have a question to auto-submit
  const question = cta.context?.question || cta.label;

  if (question && conversationId) {
    // Use the same auto-submit behavior as question type CTAs
    window.dispatchEvent(new CustomEvent('submit-question', {
      detail: {
        query: question,
        conversationId
      }
    }));
    return;
  }

  // Fallback: Focus input if no question available
  const messageInput = document.querySelector('textarea[placeholder], input[placeholder]');

  if (messageInput) {
    messageInput.focus();

    const placeholder = getPlaceholderText(cta.context);
    if (placeholder) {
      const originalPlaceholder = messageInput.placeholder;
      messageInput.placeholder = placeholder;

      setTimeout(() => {
        if (messageInput.value === '') {
          messageInput.placeholder = originalPlaceholder;
        }
      }, 10000);
    }

    messageInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }
};

/**
 * Get appropriate placeholder text based on CTA context
 */
const getPlaceholderText = (context) => {
  if (!context) return null;

  switch (context.type) {
    case 'general_inquiry':
      return 'Hva vil du vite mer om...';
    case 'support_search':
      return 'Spør om støtteordninger som kan passe for dere...';
    case 'diagnosis_specific':
      return `Spør om støtte relatert til ${context.diagnosis}...`;
    case 'educational':
      return 'Spør om skole og utdanningsstøtte...';
    default:
      return 'Fortsett samtalen...';
  }
};

/**
 * Handle application type CTAs
 * All hjelpestonad CTAs now navigate to the wizard flow
 */
const handleApplicationCTA = (cta, conversationId) => {
  const applicationId = cta.applicationId || 'hjelpestonad';

  // Route to new wizard-based soknad flow
  if (applicationId === 'hjelpestonad') {
    window.location.href = '/soknad/hjelpestonad';
    return;
  }

  // Future: handle other application types
  console.warn('[CTA Handler] Unsupported application type:', applicationId);
};

/**
 * Handle question type CTAs - AUTO-SUBMIT behavior
 * Dispatches event for ConversationView to submit the question
 */
const handleQuestionCTA = (cta, conversationId) => {
  const question = cta.context?.question || cta.label;

  if (!question) {
    console.warn('[CTA Handler] Question CTA missing question text:', cta);
    return;
  }

  // Dispatch custom event for ConversationView to handle
  window.dispatchEvent(new CustomEvent('submit-question', {
    detail: {
      query: question,
      conversationId
    }
  }));
};

/**
 * Handle resource type CTAs
 * - PDF: Fetch download URL and trigger download
 * - Video: Open video player modal
 */
const handleResourceCTA = async (cta) => {
  const { resourceId, resourceType } = cta;

  if (!resourceId) {
    console.error('[CTA Handler] Resource CTA missing resourceId:', cta);
    return;
  }

  if (resourceType === 'video') {
    // Dispatch event to open video modal
    window.dispatchEvent(new CustomEvent('open-video-modal', {
      detail: { resourceId }
    }));
    return;
  }

  // Default: PDF download
  try {
    const { getAuthToken } = await import('./auth');
    const token = await getAuthToken();

    if (!token) {
      console.error('[CTA Handler] Failed to acquire auth token for resource download');
      alert('Kunne ikke hente autentiseringstoken. Vennligst logg inn på nytt.');
      return;
    }

    const response = await fetch(`/api/resources/${resourceId}/download`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    if (!response.ok) {
      throw new Error('Failed to get download URL');
    }

    const { url, resource } = await response.json();

    // Trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = resource.title || 'resource';
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

  } catch (error) {
    console.error('[CTA Handler] Error downloading resource:', error);
    alert('Kunne ikke laste ned ressursen. Vennligst prøv igjen.');
  }
};

/**
 * Handle external link CTAs
 * Direct navigation without confirmation dialog
 */
const handleExternalCTA = (cta) => {
  const url = cta.url || cta.context?.url;

  if (!url) {
    console.error('[CTA Handler] External CTA missing URL:', cta);
    return;
  }

  // Direct navigation - no confirmation dialog
  window.open(url, '_blank', 'noopener,noreferrer');
};
